import { useEffect, useRef, useState } from "react";

function scrollToHeroAndRipple() {
  window.scrollTo({ top: 0, behavior: "smooth" });
  setTimeout(() => {
    window.dispatchEvent(new CustomEvent("ava-ripple-stores"));
  }, 800);
}

const plans = [
  {
    name: "Free",
    price: "$0",
    period: "forever",
    description: "Perfect for getting started",
    popular: false,
    features: [
      "Up to 10 chats per day",
      "Text & voice conversations",
      "Basic emotional support",
      "Standard response time",
      "Community support",
    ],
    cta: "Get Started Free",
    ctaStyle: "outline" as const,
  },
  {
    name: "Monthly",
    price: "$14.99",
    period: "per month",
    description: "Full access, month by month",
    popular: true,
    features: [
      "Unlimited chats — no daily limits",
      "Priority responses — faster replies",
      "Deep relationship insights powered by AI",
      "Voice & text conversations",
      "Advanced emotional awareness",
      "Conversation memory & continuity",
      "Personalized growth tracking",
      "Avatar customization",
      "Priority customer support",
    ],
    cta: "Start Monthly Plan",
    ctaStyle: "primary" as const,
  },
  {
    name: "Yearly",
    price: "$99.00",
    period: "per year",
    description: "Best value — save over 45%",
    popular: false,
    badge: "Save 45%",
    features: [
      "Everything in Monthly plan",
      "Unlimited chats — no daily limits",
      "Priority responses — fastest replies",
      "Deep relationship insights powered by AI",
      "Voice & text conversations",
      "Advanced emotional awareness",
      "Conversation memory & continuity",
      "Personalized growth tracking",
      "Avatar customization",
      "Priority customer support",
      "Exclusive early access to new features",
    ],
    cta: "Start Yearly Plan",
    ctaStyle: "primary" as const,
  },
];

export default function Pricing() {
  const [hoveredPlan, setHoveredPlan] = useState<number | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("pricing-visible");
          }
        });
      },
      { threshold: 0.1 }
    );
    const items = sectionRef.current?.querySelectorAll(".pricing-animate");
    items?.forEach((c) => observer.observe(c));
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="pricing"
      className="py-14 md:py-20" style={{ background: "linear-gradient(180deg, #f5f0ff 0%, #ede8ff 60%, #f0eeff 100%)" }}
    >
      <style>{`
        .pricing-animate {
          opacity: 0;
          transform: translateY(28px);
          transition: opacity 0.6s ease, transform 0.6s ease;
        }
        .pricing-animate.pricing-visible {
          opacity: 1;
          transform: translateY(0);
        }
        .pricing-animate:nth-child(1) { transition-delay: 0s; }
        .pricing-animate:nth-child(2) { transition-delay: 0.12s; }
        .pricing-animate:nth-child(3) { transition-delay: 0.24s; }
      `}</style>

      <div className="max-w-6xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#7C3AED]/20 bg-[#f5f0ff] text-[#7C3AED] text-sm font-medium mb-4">
            <i className="ri-vip-crown-line"></i>
            PRICING
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-[#1a1040] mb-3 leading-tight">
            Simple, transparent
            <br />
            <span
              className="text-transparent bg-clip-text"
              style={{ backgroundImage: "linear-gradient(90deg, #7C3AED, #A855F7)" }}
            >
              pricing.
            </span>
          </h2>
          <p className="text-[#5a4a8a] text-base max-w-lg mx-auto">
            Start free. Upgrade when you&apos;re ready. No hidden fees, no surprises.
          </p>
        </div>

        {/* Plans */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6">
          {plans.map((plan, i) => (
            <div
              key={i}
              className={`pricing-animate relative rounded-3xl border p-6 md:p-7 transition-all duration-300 ${
                plan.popular
                  ? "border-[#7C3AED]/30 bg-[#faf8ff]"
                  : "border-[#e8e0ff] bg-white hover:border-[#7C3AED]/20"
              }`}
              style={{
                transform: hoveredPlan === i ? "translateY(-6px)" : "translateY(0)",
                boxShadow: hoveredPlan === i
                  ? plan.popular
                    ? "0 16px 40px rgba(124,58,237,0.12)"
                    : "0 12px 32px rgba(124,58,237,0.08)"
                  : plan.popular
                    ? "0 8px 24px rgba(124,58,237,0.08)"
                    : "none",
              }}
              onMouseEnter={() => setHoveredPlan(i)}
              onMouseLeave={() => setHoveredPlan(null)}
            >
              {/* Popular badge */}
              {plan.popular && (
                <div
                  className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold text-white"
                  style={{ background: "linear-gradient(135deg, #7C3AED, #A855F7)" }}
                >
                  Most Popular
                </div>
              )}

              {/* Save badge */}
              {plan.badge && (
                <div className="absolute -top-3 right-4 px-3 py-1 rounded-full text-xs font-bold text-[#059669] bg-[#ecfdf5] border border-[#a7f3d0]">
                  {plan.badge}
                </div>
              )}

              {/* Plan header */}
              <div className="mb-5">
                <h3 className="font-display text-xl font-bold text-[#1a1040] mb-1">{plan.name}</h3>
                <p className="text-[#7a6aaa] text-sm">{plan.description}</p>
              </div>

              {/* Price */}
              <div className="mb-5 pb-5 border-b border-[#e8e0ff]">
                <div className="flex items-baseline gap-1">
                  <span className="font-display text-4xl font-bold text-[#1a1040]">{plan.price}</span>
                  <span className="text-[#7a6aaa] text-sm">{plan.period}</span>
                </div>
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-7">
                {plan.features.map((feature, fi) => (
                  <li key={fi} className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5" style={{ background: plan.popular ? "#ede8ff" : "#f5f0ff" }}>
                      <i className="ri-check-line text-xs" style={{ color: plan.popular ? "#7C3AED" : "#A855F7" }}></i>
                    </div>
                    <span className="text-[#5a4a8a] text-sm leading-relaxed">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <button
                type="button"
                onClick={scrollToHeroAndRipple}
                className={`w-full whitespace-nowrap flex items-center justify-center gap-2 px-5 py-3.5 rounded-full font-semibold text-sm cursor-pointer transition-all hover:scale-[1.02] ${
                  plan.ctaStyle === "primary"
                    ? "text-white"
                    : "text-[#7C3AED] border-2 border-[#7C3AED]/30 hover:bg-[#7C3AED]/5"
                }`}
                style={
                  plan.ctaStyle === "primary"
                    ? { background: "linear-gradient(135deg, #7C3AED, #A855F7)" }
                    : {}
                }
              >
                {plan.cta}
              </button>
            </div>
          ))}
        </div>

        {/* Trust note */}
        <div className="text-center mt-10">
          <div className="inline-flex items-center gap-6 text-sm text-[#7a6aaa] flex-wrap justify-center">
            <span className="flex items-center gap-1.5">
              <i className="ri-shield-check-line text-[#7C3AED]"></i>
              Secure payments
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-close-circle-line text-[#7C3AED]"></i>
              Cancel anytime
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}